import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { EnumSelector, ITypedResponse } from '@caslink/common-client';
import * as _ from 'lodash';
import { ControlService } from '../control/control.service';
import { DeviceService } from '../shared/services/device.service';
import { RtuCurrentInfoService } from './rtu-current-info.service';
import { RtuStaticScheduleService } from '../shared/services/rtu-static-schedule.service';
import {
    ControlMode, ControlModeString, IRtuCurrentInfoViewModel, IScheduleSetpointModel, ISetpointModel
} from '../shared/models/models';
import { IRtuSetpointEditorInputs } from './rtu-setpoint-editor-inputs.interface';

/**
 * Allow editing of setpoints from the RTU Schedule Calendar view
 * ***AND*** editing of Thermostat settings from RTU Thermostat view
 */

@Component({
    selector: 'dl-rtu-setpoint-editor',
    templateUrl: './rtu-setpoint-editor.component.html',
    styles: [
            `
        .dialog {
          margin-right: -20px;
        }
        `
    ]
})
export class RtuSetpointEditorComponent implements OnInit {
    public static $name: string = 'rtuSetpointEditor';

    @Output() public cancelSetpointEditor: EventEmitter<void> = new EventEmitter();

    /* State parameters from URI */
    @Input() public areaId: number;
    @Input() public hardwareItemId: number;
    @Input() public locationId: number;
    @Input() public isDialog: boolean = false;

    @Input() public currentSetpoint: ISetpointModel;
    @Input() public override: boolean;

    @Output() public onSave: EventEmitter<ISetpointModel> = new EventEmitter();

    public setpointEditorForm: FormGroup;

    /** Possible options for Control Mode selector */
    public modeSelector: EnumSelector[] = [
        {
            name: 'Heat/Cool',
            enumVal: ControlMode.FullControl,
            isVisible: () => this.evaluateVisibility('FullControl')
        },
        {
            name: 'Heating',
            enumVal: ControlMode.FullControlHeatOnly,
            isVisible: () => this.evaluateVisibility('FullControlHeatOnly')
        },
        {
            name: 'Cooling',
            enumVal: ControlMode.FullControlCoolOnly,
            isVisible: () => this.evaluateVisibility('FullControlCoolOnly')
        },
        {
            name: 'External',
            enumVal: ControlMode.External,
            isVisible: () => this.evaluateVisibility('External')
        },
        {
            name: 'Emergency Heat',
            enumVal: ControlMode.EmergencyHeat,
            isVisible: () => this.evaluateVisibility('EmergencyHeat')
        },
        {
            name: 'Off',
            enumVal: ControlMode.Off,
            isVisible: () => this.evaluateVisibility('Off')
        }
    ];

    /* Setpoint parameters from currentInfo call */
    allowApplyToArea: boolean;
    allowApplyToSite: boolean;
    copyToVisible: boolean = false;

    inputs: IRtuSetpointEditorInputs;
    isReady: boolean = false;

    private applyToArea: boolean;
    private applyToSite: boolean;

    /** The response from the server from getRtuCurrentInfo */
    private currentInfoModel: IRtuCurrentInfoViewModel;

    constructor(
        private controlService: ControlService,
        private deviceService: DeviceService,
        private rtuCurrentInfoService: RtuCurrentInfoService,
        private rtuStaticScheduleService: RtuStaticScheduleService,
        private cdr: ChangeDetectorRef
    ) {
    }

    public ngOnInit() {
        this.setupEditor();
    }

    cancel() {
        this.cancelSetpointEditor.emit();
    }
    overrideSchedule() {
        const overrideModel: ISetpointModel = {};

        this.mapInputs(overrideModel);

        this.controlService.broadcastCardStatusUpdate({
            locationId: this.locationId,
            hardwareItemId: this.hardwareItemId,
            status: 'Submitting'
        });

        this.rtuStaticScheduleService
            .overrideSchedule(this.locationId, this.areaId, this.hardwareItemId, overrideModel['overrideMinutes'], overrideModel)
            .then(() => {
                // do nothing
            });
    }

    save() {
        const currentSetpoint: ISetpointModel = this.currentSetpoint;
        const newSetpoint: IScheduleSetpointModel = {};

        this.mapInputs(newSetpoint);

        /* Make sure we overwrite the existing setpoint, if it exists, rather than always creating a new one. */
        if (currentSetpoint.name) {
            newSetpoint.name = currentSetpoint.name;
        }

        this.controlService.broadcastCardStatusUpdate({
            locationId: this.locationId,
            hardwareItemId: this.hardwareItemId,
            status: 'Submitting'
        });

        this.controlService.upsertSetpoint(this.hardwareItemId, newSetpoint).then((data: ITypedResponse<IScheduleSetpointModel>) => {
            if (data.success) {
                const setpoint: IScheduleSetpointModel = data.response;

                /** If we don't do this we won't know what color to apply to the new setpoint class, and it won't be able to be saved */
                this.rtuStaticScheduleService.addCssClassFromSetpoint(setpoint);

                this.onSave.emit(setpoint);
            }
        });
    }

    /**
     * Determine whether or not to show the 'Blower' selection.
     * Currently, in the old app, the function is identical to showOccupied(), so we'll just use that here,
     * but it's a separate function for clarity in case it needs to change implementations
     */
    showBlower() {
        return this.showOccupied();
    }

    /** Determine whether or not to show the 'Cooling Setpoint' selection */
    showCooling() {
        return this.checkControlMode([ControlMode.FullControlCoolOnly, ControlMode.FullControl, ControlMode.External]);
    }

    /** Determine whether or not to show the 'Use External Efficiency Setpoints' selection. */
    showUseExternalEfficiencySetpoints() {
        return this.checkControlMode([ControlMode.External]);
    }

    toggleCopyToVisible() {
        this.copyToVisible = !this.copyToVisible;
    }

    /** Determine whether or not to show the 'Heating Setpoint' selection */
    showHeating() {
        return this.checkControlMode([ControlMode.FullControlHeatOnly, ControlMode.FullControl, ControlMode.External]);
    }

    /** Determine whether or not to show the 'Occupied' selection */
    showOccupied() {
        const occupiedControlModes = [
            ControlMode.FullControlHeatOnly,
            ControlMode.FullControlCoolOnly,
            ControlMode.FullControl,
            ControlMode.External
        ];

        return this.checkControlMode(occupiedControlModes);
    }

    /* Shortcut method for doing all of the computations for showing or hiding things based on control mode */
    private checkControlMode(allowedModes: ControlMode[]) {
        /* On the first run, this should use the passed-in setpoint. After that, use the values of the inputs */
        const inputs = this.inputs;
        const setpoint = this.currentSetpoint;

        const controlMode = inputs ? inputs.controlMode.value : setpoint.controlMode;

        return controlMode;

        // allowedModes.indexOf(controlMode) > -1;
    }

    /**
     * Would have liked to use the original enum for this,
     * but it appears impossible, as you cannot access a const enum without a string literal,
     * but without const, references will not compile to ints
     */
    private evaluateVisibility(controlMode: ControlModeString) {
        const model = this.currentInfoModel;

        if (!model) {
            return false;
        }

        return model.controlModeSelections.items.some(item => item.value === controlMode);
    }

    /**
     * Using the identical keys, we want to map back from the form inputs to another setpoint object
     * so that we don't modify the original setpoint
     */
    private mapInputs(obj: any) {
        Object.keys(this.inputs).map((key: string) => {
            obj[key] = this.inputs[key].value;
        });
    }

    /* Given the return value from the currentInfo call, set up visible controls */
    private setCurrentParametersInfo(model: IRtuCurrentInfoViewModel) {
        this.allowApplyToArea = model.applyToArea.isVisible;
        this.allowApplyToSite = model.applyToSite.isVisible;
        this.applyToArea = false;
        this.applyToSite = false;

        this.currentInfoModel = model;
    }

    private setupEditor() {
        let validators = [
            this.validateTemperatures()
        ];

        this.setpointEditorForm = new FormGroup({}, validators);

        this.isReady = false;

        this.rtuCurrentInfoService.getRTUCurrentInfo(this.locationId, this.areaId, this.hardwareItemId)
            .then((currentInfoModel: IRtuCurrentInfoViewModel) => {
                this.setCurrentParametersInfo(currentInfoModel);

                this.setupFormModel();

                this.isReady = true;
                this.cdr.detectChanges();
            });
    }

    private setupFormModel() {
        /* Base input definitions */

        const visibleModes = this.modeSelector.filter((mode: EnumSelector) => mode.isVisible());

        const minTemp = 0;
        const maxTemp = 150;

        const defaultCoolingTemp = 75;
        const defaultHeatingTemp = 65;

        const initialCoolingTemp = Number(this.currentSetpoint.coolingTemp) || defaultCoolingTemp;
        const initialHeatingTemp = Number(this.currentSetpoint.heatingTemp) || defaultHeatingTemp;

        const coolingTempDisplayText = this.showUseExternalEfficiencySetpoints() ? 'Upper Limit' : 'Cooling Setpoint';
        const heatingTempDisplayText = this.showUseExternalEfficiencySetpoints() ? 'Lower Limit' : 'Heating Setpoint';

        this.inputs = {
            applyToArea: {
                minValue: 0,
                maxValue: 0,
                value: this.applyToArea ? 1 : 0,
                $inputType: 'SelectList',
                $fieldName: 'applyToArea',
                $type: '',
                displayText: 'Apply To Area',
                isEnabled: true,
                isVisible: true,
                elementType: '',
                $ignoreChildren: true,
                $valueList: [
                    {value: 0, displayText: 'NO'},
                    {value: 1, displayText: 'YES'}
                ]
            },
            applyToSite: {
                minValue: 0,
                maxValue: 0,
                value: this.applyToSite ? 1 : 0,
                $inputType: 'SelectList',
                $fieldName: 'applyToSite',
                $type: '',
                displayText: 'Apply To Site',
                isEnabled: true,
                isVisible: true,
                elementType: '',
                $ignoreChildren: true,
                $valueList: [
                    {value: 0, displayText: 'NO'},
                    {value: 1, displayText: 'YES'}
                ]
            },
            controlMode: {
                minValue: 0,
                maxValue: 0,
                value: !_.isUndefined(this.currentSetpoint.controlMode) ?
                    this.currentSetpoint.controlMode :
                    visibleModes[0].enumVal,
                $inputType: 'SelectList',
                $fieldName: 'controlMode',
                $type: '',
                displayText: 'Mode',
                isEnabled: true,
                isVisible: true,
                elementType: '',
                $ignoreChildren: true,
                $valueList: visibleModes.map((mode: EnumSelector) => {
                    return {
                        value: mode.enumVal,
                        displayText: mode.name
                    };
                })
            },
            occupied: {
                minValue: 0,
                maxValue: 0,
                value: this.currentSetpoint.occupied ? 1 : 0,
                $inputType: 'SelectList',
                $fieldName: 'occupied',
                $type: '',
                displayText: 'Occupied',
                isEnabled: true,
                isVisible: true,
                elementType: '',
                $ignoreChildren: true,
                $valueList: [
                    {
                        value: 0,
                        displayText: 'NO'
                    },
                    {
                        value: 1,
                        displayText: 'YES'
                    }
                ]
            },
            blowerMode: {
                minValue: 0,
                maxValue: 0,
                value: this.currentSetpoint.blowerMode ? 1 : 0,
                $inputType: 'SelectList',
                $fieldName: 'blowerMode',
                $type: '',
                displayText: 'Blower Mode',
                isEnabled: true,
                isVisible: true,
                elementType: '',
                $ignoreChildren: true,
                $valueList: [
                    {
                        value: 0,
                        displayText: 'Auto'
                    },
                    {
                        value: 1,
                        displayText: 'On'
                    }
                ]
            },
            useExternalEfficiencySetpoints: {
                minValue: 0,
                maxValue: 0,
                value: this.currentSetpoint.useExternalEfficiencySetpoints ? 1 : 0,
                $inputType: 'SelectList',
                $fieldName: 'useExternalEfficiencySetpoints',
                $type: '',
                displayText: 'Use External Efficiency Setpoints',
                isEnabled: true,
                isVisible: true,
                elementType: '',
                $ignoreChildren: true,
                $valueList: [
                    {
                        value: 0,
                        displayText: 'NO'
                    },
                    {
                        value: 1,
                        displayText: 'YES'
                    }
                ]
            },
            coolingTemp: {
                minValue: minTemp,
                maxValue: maxTemp,
                value: initialCoolingTemp,
                $allowInvalid: true,
                $customValidatorMessage: `${coolingTempDisplayText} must be at least 2 degrees higher than ${heatingTempDisplayText}`,
                $inputType: 'NumberSensor',
                $fieldName: 'coolingTemp',
                $type: '',
                $useAngularMinMax: true,
                displayText: coolingTempDisplayText,
                isEnabled: true,
                isVisible: true,
                elementType: 'TemperatureSensor',
                $ignoreChildren: true
            },
            heatingTemp: {
                minValue: minTemp,
                maxValue: maxTemp,
                value: initialHeatingTemp,
                $allowInvalid: true,
                $inputType: 'NumberSensor',
                $fieldName: 'heatingTemp',
                $type: '',
                $useAngularMinMax: true,
                displayText: heatingTempDisplayText,
                isEnabled: true,
                isVisible: true,
                elementType: 'TemperatureSensor',
                $ignoreChildren: true
            },
            overrideMinutes: {
                minValue: 30,
                maxValue: 300,
                value: 60,
                $inputType: 'NumberSensor',
                $fieldName: 'overrideMinutes',
                $type: '',
                displayText: 'Override Minutes',
                isEnabled: true,
                isVisible: true,
                elementType: 'OverrideMinutesSensor',
                $ignoreChildren: true
            }
        };

        /* Apply the mappings */
        for (const key in this.inputs) {
            const currentInput = this.inputs[key];

            if (currentInput.elementType !== '') {
                this.deviceService.setControlInputType(currentInput);
            }
        }
    }

    private validateTemperatures() {
        return (control: AbstractControl) => {
            if (!this.inputs) {
                return null;
            }

            let heatingTempControl = control.get(this.inputs.heatingTemp.$fieldName);
            let coolingTempControl = control.get(this.inputs.coolingTemp.$fieldName);

            if (!(heatingTempControl && coolingTempControl)) {
                return null;
            }

            const minimumDifference = 2;

            let hasError = coolingTempControl.value - heatingTempControl.value < minimumDifference;

            if (hasError) {
                let error = {custom: {value: control.value}};
                heatingTempControl.setErrors(error);
                coolingTempControl.setErrors(error);
            } else {
                heatingTempControl.setErrors(null);
                coolingTempControl.setErrors(null);
            }

            return null;
        };
    }
}
